import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import {
  Injectable,
  OnDestroy,
  Renderer2,
  RendererFactory2,
  effect,
  inject,
  signal
} from '@angular/core';
import { raygunHttpError } from '@approot/app.raygun.setup';
import { BrandingService } from '@approot/branding';
import {
  AdjustQuoteForMargin,
  AlertType,
  DefaultHedges,
  DiscountFactorResponse,
  GenericLeg,
  KeyValue,
  MedusaCodeAndType,
  MedusaUser,
  MultiHedge,
  OneWayView,
  RFQ_STATUS_BLOCKED,
  RfqSettingsState,
  RfqSettingsStateKey,
  RfqStatus,
  RfqTab,
  SubAccount
} from '@approot/models';
import { ApiErrorMeta } from '@approot/shared/api-error';
import {
  FX_API_PATHS,
  apiURL,
  clone,
  dvSchemas,
  getUserInfoURL,
  isFeatureEnabled,
  shortUuid,
  sleep
} from '@approot/utilities';
import dayjs from 'dayjs';
import { get, pick } from 'lodash';
import { Throttle } from 'lodash-decorators';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  firstValueFrom,
  forkJoin,
  of,
  throwError
} from 'rxjs';
import {
  catchError,
  delay,
  exhaustMap,
  filter,
  map,
  switchMap,
  tap
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AdminService } from '../admin/admin.service';
import { AlertsService } from '../alerts/alerts.service';
import { AuthService, UserSession } from '../auth/auth.service';
import { CloudwatchLogService } from '../cloudwatch-logs/cloudwatch-logs.service';
import {
  ConfigService,
  TradeBookingParameters
} from '../config/config.service';
import { EntityMapperService } from '../entity-mapper/entity-mapper.service';
import { LegService } from '../leg/leg.service';
import { PhoenixEventBusService } from '../phoenix/phoenix-event-bus.service';
import {
  DefaultGetEventsState,
  PhoenixService
} from '../phoenix/phoenix.service';
import { ProductsConfigService } from '../products-config/products-config.service';
import { TabServiceRegistryService } from '../tab-service-registry.service';
import { TradesCaptureService } from '../trade-capture/trades-capture.service';
import { UserStorageService } from '../user-storage/user-storage.service';
import {
  calcSideToShowOneWay,
  getTabTitleCross,
  isFirstGenExotic,
  rfqLegs,
  rfqTabTitleMapper,
  setPositionToUse
} from './rfq-creation';
import {
  PresetSetupData,
  RfqOutcome,
  positionsHandler
} from './rfq-tab/rfq-tab';
import { RFQDetailsDTO } from './rfq-tab/rfq-tab.service';
import {
  Account,
  BooleanResponse,
  BreakDownVanillaStrategy,
  ClientQuoteEventExtended,
  ConnectionState,
  EventNotificationEvents,
  EventNotificationResult,
  ExecutionContext,
  ExecutionContextPayload,
  KeyValueResponse,
  LastGetEvents,
  LiveSpotResponse,
  LoginPayload,
  LoginResponse,
  MedusaRequestOptions,
  Provider,
  QuoteEvent,
  RequestResponse,
  RfqBuilderLeg,
  RfqDetails,
  RfqTimelineEvent,
  RfqTradeCaptureParams,
  UserRole,
  ValidateRfq,
  VolBasedHedgeExecutionPayload
} from './rfq.types';
type SubmitRequest = dvSchemas['SubmitRequest'] & { isPerseus: boolean };

type Outcome = {
  outcome: {
    level?: AlertType;
    message: string;
  };
};

const ERROR_TYPE_MAP = new Map([
  ['MaxTenorExceeded', 'maximum tenor exceeded']
]);

@Injectable({
  providedIn: 'root'
})
export class RfqService implements OnDestroy {
  public executionContext: ExecutionContext;
  public readonly renderer: Renderer2;
  // public requestNextEventSubscription: Subscription;
  public latestGetEventsSubscription: Subscription;
  public readonly currentUserRole = signal<UserRole>(null);
  public blockNewRfqRequests = false; //blocks tabs from making an rfq
  public readonly events$: Observable<EventNotificationEvents>;
  public readonly rfqTabs$: Observable<RfqTab[]>;
  public readonly pollEventResponse$: Observable<any>;
  public readonly loginState$: Observable<boolean>;
  public currentTimestamp: number;
  public currentUserId: string;
  public reLoginRequired = false;
  public isVolBased = false;
  public isPremiumBasedVanillasWithSpotFeed = false;
  public directRfqs: RfqTab[] = [];
  public useSpotInRfqPanel: boolean;
  public initialEntityAccounts: Account[];
  private _rfqTabs: RfqTab[] = []; // stores all the tab information
  private readonly loginStateSubject = new ReplaySubject<boolean>(1);
  private readonly rfqTabsSubject = new BehaviorSubject<RfqTab[]>(null);
  // Replay count is arbitrary - might need tweaking (better too big than too small)
  private readonly eventsSubject = new ReplaySubject<EventNotificationEvents>(
    512
  );
  private readonly pollEventResponseSubject: Subject<any> = new Subject();
  // private readonly requestNextEventSubject = new Subject();
  private _subscribedToRfqs = false;
  private _providers: Provider[];
  private _events: QuoteEvent[] = [];
  private _currentTimestampInterval: any;
  private _warningScreenData = {
    warningScreenOpen: false,
    messageToDisplay: '',
    headerMessage: ''
  };
  private blotterRefreshInterval: Subscription;
  private suppressErrorAlerts: boolean;
  private lastEventTimestamp = 0;
  private isPhxEnabled = false;
  private availableProviders: dvSchemas['AccountPrimeBrokerAvailableProviders'];
  public readonly connectionState = signal<ConnectionState>('INITIAL');
  public readonly lastGetEvents = signal<LastGetEvents>({
    diff: null,
    visibility: 'visible',
    loggedIn: false
  });
  public readonly displayedAccount = signal<string>(null);
  public staleGetEventsInterval: any;
  private getEventsPollerInterval: any;
  private getEventsRunning = false;
  protected phxService = inject(PhoenixService);
  protected authService = inject(AuthService);
  private http = inject(HttpClient);
  private alertsService = inject(AlertsService);
  private rendererFactory = inject(RendererFactory2);
  private adminService = inject(AdminService);
  private tradesCaptureService = inject(TradesCaptureService);
  private cwLogSvc = inject(CloudwatchLogService);
  private configService = inject(ConfigService);
  private legService = inject(LegService);
  private productsConfigService = inject(ProductsConfigService);
  private userStorageService = inject(UserStorageService);
  private entityMapperService = inject(EntityMapperService);
  private brand = inject(BrandingService);
  private phxBus = inject(PhoenixEventBusService);
  private tabServiceRegistryService = inject(TabServiceRegistryService);
  private alertService = inject(AlertsService);
  private enableMedusaJWTLogin: boolean;

  constructor() {
    this.pollEventResponse$ = this.pollEventResponseSubject.asObservable();
    this.rfqTabs$ = this.rfqTabsSubject.asObservable();
    this.loginState$ = this.loginStateSubject.asObservable();
    this.events$ = this.eventsSubject.asObservable();
    this.renderer = this.rendererFactory.createRenderer(null, null); // we want to inject renderer into the service
    this.isPhxEnabled = this.phxService.isEnabled();
    this.loginStateSubject.next(false);
    this.enableMedusaJWTLogin = isFeatureEnabled('enableMedusaJWTLogin', false);

    this.staleGetEventsInterval = setInterval(
      () => this.pollStaleGetEvents(),
      3_000
    );

    effect(() => {
      this.phxService.getEventsPoll();
      try {
        firstValueFrom(this.getEvents());
      } catch {}
    });

    effect(() => {
      if (this.authService.logoutSignal()) {
        this.medusaLogout();
      }
    });

    this.phxService.sendGetEventsState(DefaultGetEventsState);

    effect(() => {
      this.phxService.getEventsStatePoll();

      const loggedIn = this.adminService.isMedusaAccountLoggedIn;

      const diff =
        loggedIn && this.lastEventTimestamp
          ? Date.now() - this.lastEventTimestamp
          : null;
      const payload = {
        diff,
        visibility: document.visibilityState,
        loggedIn,
        medusaUser: this.adminService.medusaAccount.userId,
        medusaEntity: this.adminService.medusaAccount.entity,
        userRole: loggedIn ? this.currentUserRole() : null
      };

      this.phxService.sendGetEventsState(payload);
    });

    this.startGetEventsPoller();
  }

  public ngOnDestroy(): void {
    // Service should never be destroyed, but play it safe
    clearInterval(this.staleGetEventsInterval);
    clearInterval(this.getEventsPollerInterval);
  }

  public startGetEventsPoller() {
    console.log('startGetEventsPoller', this.adminService.medusaAccount.userId);
    let running = false;
    let firstRun = true;
    this.getEventsPollerInterval = setInterval(async () => {
      if (running || !this.adminService.medusaAccount.userId) {
        return;
      }

      if (firstRun) {
        try {
          // Sleep a bit to prevent possible race condition with Perseus
          await sleep(500);
          const userRole = await firstValueFrom(
            this.userRole('getEventsPoller')
          );
          // There's a slight race condition whereby the medusaAccount can be set but
          // the actual login request has not yet completed.
          if (!userRole?.result) {
            console.warn('getEventsPoller no userRole');
            return;
          }
          firstRun = false;
        } catch {}
      }

      running = true;
      try {
        const events = await firstValueFrom(this.getEvents());
        this.connectionState.set('CONNECTED');
        // Strangely, when the network is disconnected, getEvents() doesn't error,
        // but returns null
        if (!events) {
          firstRun = true;
          this.connectionState.set('DISCONNECTED');
          console.log('startGetEventsPoller no response');
        }
      } catch (e) {
        firstRun = true;
        this.connectionState.set('DISCONNECTED');
        console.log(e);
      }
      running = false;
    }, 500);
  }

  private pollStaleGetEvents() {
    if (
      this.lastEventTimestamp === 0 ||
      !this.adminService.medusaAccount.userId
    ) {
      this.lastGetEvents.set({
        ...DefaultGetEventsState,
        visibility: document.visibilityState
      });
      return;
    }

    const diff = Date.now() - this.lastEventTimestamp;
    const payload = {
      diff,
      visibility: document.visibilityState,
      loggedIn: this.adminService.isMedusaAccountLoggedIn
    };

    this.lastGetEvents.set(payload);
    this.logStaleGetEvents(diff);
  }

  @Throttle(20_000)
  private logStaleGetEvents(diff: number) {
    const minDiff = document.visibilityState === 'hidden' ? 60_000 : 10_000;
    const maxDiff = 3 * 60_000; // computer probably hibernated or laptop closed.
    const diffSeconds = (diff / 1000).toFixed(1);
    if (diff > minDiff && diff < maxDiff) {
      console.warn(`getEvents stale: ${diff}ms`);
      this.phxService.logEvent({
        type: 'rfq',
        category: 'staleGetEvents',
        diff,
        message: `No events received for ${diffSeconds}s [window: ${document.visibilityState}]`
      });
    }
  }

  public get warningScreenData() {
    return this._warningScreenData;
  }

  public get providers() {
    return this._providers;
  }

  public set providers(providers: Provider[]) {
    this._providers = providers || [];
  }

  public logUserIn(
    loginPayload: LoginPayload,
    pollEvents = false
  ): Observable<any> {
    this.suppressErrorAlerts = false; // we want to show errors
    this.blotterRefreshInterval?.unsubscribe();
    this.adminService.medusaAccount.userId = loginPayload.user;
    this.adminService.medusaAccount.entity = loginPayload.entity;

    return this.login({
      user: loginPayload.user,
      password: loginPayload.password,
      userId: loginPayload.userId
    }).pipe(
      exhaustMap(
        (loginResponse: { result: Required<dvSchemas['LoginToken']> }) => {
          if (loginResponse?.result?.sessionId) {
            this.reLoginRequired = false;
            this.adminService.medusaAccount = {
              ...loginResponse.result,
              entity: loginPayload.entity
            };
            this.startRecordingCurrentTimeStamp();
            const calls = {
              todaysTrades: this.getTodaysTrades(),
              providers: this.getProviders(),
              accounts:
                this.currentUserRole() === 'ProviderTrader' ||
                this.configService.userSettings().isWhiteLabelUser
                  ? of({ result: [] })
                  : this.getClientAccounts()
            };
            this.loginStateSubject.next(true);
            this.connectionState.set('CONNECTED');

            return forkJoin(calls);
          } else {
            if (!loginResponse.result) {
              // we want to show the login text box
              this.adminService.showMedusaLoginErrorModal = true;
            }
            this.connectionState.set('DISCONNECTED');
            this.loginStateSubject.next(false);
            return throwError(loginResponse);
          }
        }
      ),
      tap((response: LoginResponse) => {
        if (response.providers) {
          this.providers = response.providers.result;
        }
        if (response.accounts) {
          this.initialEntityAccounts = response.accounts.result;
        }
        if (true || pollEvents) {
          this.subscribeToRfqs().subscribe();
          this.subscribeToEvents();
        }
      })
    );
  }

  public logoutAndClearRfq() {
    this.logout();
    this.clearAllRfqs();
  }

  public startPollEvents(): void {
    this.subscribeToRfqs().subscribe();
    this.subscribeToEvents();
  }

  private clearSelectedMedusaAccount() {
    this.adminService.medusaAccount = {
      entity: null,
      userId: null,
      sessionId: null,
      timestamp: null
    };
    this.currentUserRole.set(null);
  }

  public loginWithEntity(entity: string) {
    const credentials = this.adminService.medusaCredentials.find(
      cred => cred.entity === entity
    );
    this.logUserIn({
      user: credentials.medusaUser,
      password: credentials.password,
      userId: credentials.medusaUserId,
      entity
    }).subscribe(() => {
      this.passwordExpired().subscribe(expired => {
        if (expired) {
          this.alertsService.addAlert({
            message: `Your password has expired. Please contact ${this.brand.supportContact()} to reset it.`,
            type: 'warning',
            config: {
              disableTimeOut: true,
              closeButton: true
            }
          });
        }
      });
    }); // login with the credentials found
  }

  public addNewTab(
    builderLegs: RfqBuilderLeg[],
    productLongNameToUse: string,
    presetSetupData: PresetSetupData,
    isDirectRfq = false
  ): void {
    if (
      !this.rfqTabs.length &&
      !this.directRfqs.length &&
      !this.reLoginRequired
    ) {
      this.blockNewRfqRequests = false; // allow rfq tabs to make requests
    }
    const tab = this.makeRfqTab(
      builderLegs,
      productLongNameToUse,
      presetSetupData
    );

    if (isDirectRfq) {
      this.directRfqs.push(tab);
    } else {
      this.rfqTabs.push(tab);
      this.rfqTabsSubject.next(this.rfqTabs);
    }
    this.resizeStrategyComponent();
  }

  // legacy
  public getTodaysTrades() {
    return of({ result: [] });
  }

  // legacy
  public clearAllRfqs() {
    this._rfqTabs = [];
  }

  private startRecordingCurrentTimeStamp() {
    clearInterval(this._currentTimestampInterval);
    this._currentTimestampInterval = setInterval(() => {
      this.currentTimestamp = dayjs.utc().valueOf();
    }, 50);
  }

  private async resizeStrategyComponent() {
    await sleep(0);
    if (!(this.rfqTabs.length === 1 || this.directRfqs.length === 1)) {
      return;
    }
    // if this is the first tab, we may need to resize the strategy-detail component so that the rfq is visible on screen.
    let strategyDetailContent: any;
    try {
      strategyDetailContent = this.renderer.selectRootElement(
        '.strategy-detail-content',
        true
      );
    } catch (e) {
      // strategyDetailContent doesn't exist
      return;
    }

    const strategyDetailContentResizer = this.renderer.selectRootElement(
      '.strategy-detail-content__resize-container',
      true
    );

    const windowWidth = window.innerWidth; // width of page

    const strategyDetailStyle = window.getComputedStyle(strategyDetailContent); // style strategy detail

    const strategyDetailWidthLeftMargin = parseFloat(
      strategyDetailStyle.marginLeft
    ); // width of strategy detail left margin

    const strategyDetailWidthRightMargin = parseFloat(
      strategyDetailStyle.marginRight
    ); // width of strategy detail right margin

    const rfqContainerWidth = 1200; // width of the rfq container component

    const strategyDetailWidth = // width of the strategyDetail Component
      strategyDetailContent.offsetWidth +
      (!isNaN(strategyDetailWidthLeftMargin)
        ? strategyDetailWidthLeftMargin
        : 0) +
      (!isNaN(strategyDetailWidthRightMargin)
        ? strategyDetailWidthRightMargin
        : 0);

    const minWidthOfStrategyDetails = parseFloat(
      window.getComputedStyle(strategyDetailContent).minWidth
    ); // min width of strategy details component

    const MaxStrategySize = Math.max(
      windowWidth - rfqContainerWidth,
      minWidthOfStrategyDetails
    ); // the max width of the strategy so the rfq fits on screen. (this max should not be bellow the minimum possible width of it)

    if (
      strategyDetailWidth > MaxStrategySize || // is the width of the strategyDetail more then its minimum for the rfq to fit
      MaxStrategySize + rfqContainerWidth > windowWidth
      // if the max strategy size and the rfq container width, is bigger than the page size use the MaxStrategySize as the strategy size
    ) {
      this.renderer.setStyle(
        strategyDetailContentResizer,
        'width',
        MaxStrategySize + 'px'
      ); // set width to the max width
    }
  }

  public removeTab(index: number) {
    this.rfqTabs.splice(index, 1);
    this.rfqTabsSubject.next(this.rfqTabs);
  }

  public removeDirectRfqTab(index: number) {
    this.directRfqs.splice(index, 1);
  }

  public get rfqTabs() {
    return this._rfqTabs;
  }

  public get subscribedToRfqs() {
    return this._subscribedToRfqs;
  }

  public set subscribedToRfqs(value: boolean) {
    this._subscribedToRfqs = value;
  }

  public login(payload: {
    user: string;
    password: string;
    userId: number;
  }): Observable<{ result: dvSchemas['LoginToken'] }> {
    let loginResponse: { result: dvSchemas['LoginToken'] };
    this.currentUserId = null;

    let req: Observable<{ result: dvSchemas['LoginToken'] }>;
    if (this.enableMedusaJWTLogin && !this.isMedusaProxy && !payload.userId) {
      const p = { ...payload, password: '**************' };
      this.phxService.sendSlack(`Unexpected /login from ${JSON.stringify(p)}`);
    }
    // Disable for proxy until implemented
    if (payload.userId && this.enableMedusaJWTLogin && !this.isMedusaProxy) {
      delete payload.password;
      delete payload.user;
      req = this.switchUserHttpRequest(payload);
    } else {
      delete payload.userId;
      req = this.loginHttpRequest(payload);
    }

    return req.pipe(
      switchMap(resp => {
        if (resp.result) {
          this.currentUserId = resp.result.userId;
          loginResponse = resp;
          return this.userRole('login').pipe(
            switchMap(res => {
              if (!res.result) {
                // if userRole returns null, most likely there is a third party cookie issue.
                this.emitErrorMessage(
                  'Error after login: Please ensure that your browser allows third-party cookies (or contact your sales representative quoting this error)'
                );
                return throwError(res);
              }
              this.currentUserRole.update(_ => res.result);
              return of(loginResponse);
            })
          );
        } else {
          return of(resp);
        }
      })
    );
  }

  public subscribeToEvents() {
    if (this.latestGetEventsSubscription) {
      this.latestGetEventsSubscription.unsubscribe();
    }
    this.latestGetEventsSubscription = this.getEvents()
      .pipe(
        catchError(err => {
          this.getEventsError();
          return EMPTY;
        })
      )
      .subscribe({
        error: () => this.getEventsError()
      });
  }

  public setRfqTabGenericInfo(
    rfqTab: RfqTab,
    legs: RfqBuilderLeg[],
    newRfqTab: boolean
  ) {
    const rfqTabClone = clone(rfqTab);
    const dcdShortName = 'dcd';
    rfqTabClone.title.quotePair = getTabTitleCross(legs[0]);
    const productName = rfqTabTitleMapper(
      legs,
      this.configService.userSettings()
    );
    const shortName = productName?.shortName;
    rfqTabClone.positions = positionsHandler(shortName, legs);
    rfqTabClone.title.productName = productName;
    rfqTabClone.isFirstGenExotic = isFirstGenExotic(shortName);
    rfqTabClone.quoteOptions = {
      showPayRecieve: shortName !== dcdShortName,
      showQuotedPremiumSection: shortName !== dcdShortName
    };
    rfqTabClone.legs = rfqLegs(
      legs,
      rfqTab.static,
      newRfqTab,
      this.legService.productSpecifications
    );
    rfqTabClone.oneWayView = setOneWayMode(
      shortName,
      legs,
      this.userStorageService.getItem<RfqSettingsState>(RfqSettingsStateKey) ||
        {},
      rfqTabClone.static
    );
    rfqTabClone.positionToUse = setPositionToUse(
      legs,
      this.configService.userSettings()
    );
    Object.assign(rfqTab, rfqTabClone);
  }

  private getEventsError() {
    // on error from getEvents wait 1 second before trying again
    if (this.latestGetEventsSubscription) {
      this.latestGetEventsSubscription.unsubscribe();
      this.latestGetEventsSubscription = null;
    }
  }

  public subscribeToRfqs(): Observable<BooleanResponse> {
    return this.rfqSubscriberFactory().pipe(
      tap(response => {
        if (response.result) {
          this.subscribedToRfqs = true;
        }
      }),
      catchError(err => {
        return this.handleMedusaError(err, 'subscribeToRfqs');
      })
    );
  }

  private rfqSubscriberFactory(): Observable<BooleanResponse> {
    return this.currentUserRole() === 'ProviderTrader'
      ? this.subscribeProviderRFQs()
      : this.subscribeClientRFQs;
  }

  private getEvents(): Observable<EventNotificationResult> {
    if (this.getEventsRunning || !this.adminService.isMedusaAccountLoggedIn) {
      return of({ result: { events: [], timestamp: Date.now() } });
    }
    this.getEventsRunning = true;
    return this.getEventsHttpRequest.pipe(
      tap(() => {
        this.getEventsRunning = false;
      }),
      // after user logout, there could still be outstanding events.
      // A logout refreshes the page but occasionally get a transitory error "toast"
      filter(() => this.adminService.isMedusaAccountLoggedIn),
      tap(async response => {
        await sleep(50); // sanity delay in case of runaway errors
        let events = response.result.events as QuoteEvent[];
        const filteredEvents = this.manageEvents(events);
        this.pollEventResponseSubject.next(filteredEvents);
        this.handleGetEventsResponse(response);
      }),
      tap(response => {
        if (this.isPhxEnabled && response.result?.events?.length) {
          setTimeout(() => this.phxService.logGetEvents(response));
        }
      }),
      catchError(err => {
        this.getEventsRunning = false;
        this.phxLogError(err);
        return EMPTY;
      })
    );
  }

  @Throttle(100)
  private phxLogError(err: any) {
    setTimeout(() => {
      this.phxService.logEvent({
        type: 'rfq',
        category: 'getEventsError',
        error: err.message || err
      });
    });
  }

  private handleGetEventsResponse(response: EventNotificationResult) {
    const events: any[] = [];
    response.result.events.forEach(event => {
      // some events don't have a requestId attached and are seen as global events
      // find the events and carry them out
      if (event['@type'] === 'TradeDetailsEvent') {
        this.sendTradeToTradeService(event);
        event['processed'] = true;
        events.push(event);
      } else if (event['@type'] === 'ProviderQuoteEvent') {
        event['processed'] = true;
        events.push(event);
      } else if (
        event['@type'] === 'ClientQuoteEvent' &&
        event.action === 'PickedUp'
      ) {
        if (
          !this.configService.userSettings().parent['rfq:recordPickedUpEvents']
        ) {
          return;
        }

        this.recordPickedUpEvent(event as any);
      }
    });
    if (events.length) {
      this.eventsSubject.next(events);
    }
  }

  private sendTradeToTradeService(event: any) {
    const eventToSend = { ...event };
    const requestId = event.trade?.requestId;
    const setup =
      this.tabServiceRegistryService.findSetupByRequestId(requestId);
    if (setup === null) {
      console.warn('No setup found for requestId', requestId);
    }

    let handleMedusaTradeExecutionPayLoad: KeyValue = clone({
      task: 'handleMedusaTradeExecution',
      tradeDetailsEvents: [eventToSend],
      mappedProviderList: this.availableProviders?.mappedProviderList,
      setup
    });

    handleMedusaTradeExecutionPayLoad =
      this.entityMapperService.cleanConfidentialFields(
        handleMedusaTradeExecutionPayLoad
      );

    this.configService
      .getTradeBookingParameters()
      .pipe(
        // Speculative fix for possible race condition where
        // executedTradeInfo is not available
        delay(2000),
        switchMap(tradeBookingParameters => {
          handleMedusaTradeExecutionPayLoad.tradeBookingParameters =
            tradeBookingParameters;
          return this.tradeService(handleMedusaTradeExecutionPayLoad);
        })
      )
      .subscribe();
  }

  public handleVolBasedHedgeExecution(
    payload: VolBasedHedgeExecutionPayload
  ): Observable<TradeBookingParameters> {
    const newPayload: Required<VolBasedHedgeExecutionPayload> = {
      ...payload,
      task: 'handleVolBasedHedgeExecution',
      tradeBookingParameters: null,
      mappedProviderList: this.availableProviders?.mappedProviderList
    };

    return this.configService.getTradeBookingParameters().pipe(
      switchMap(tradeBookingParameters => {
        newPayload.tradeBookingParameters = tradeBookingParameters;
        return this.tradeService(newPayload);
      })
    );
  }

  public markEventsAsProcessedById(id: number) {
    this._events
      .filter(event => event.quote?.['id'] !== id)
      .forEach(event => {
        event.processed = true;
      });
  }

  private manageEvents(newEvents: QuoteEvent[]) {
    this._events = [
      ...this._events.filter(event => !event.processed),
      ...newEvents
    ];
    return this._events;
  }

  private blockAllTabs() {
    this.blockNewRfqRequests = true;
    this._events = [];
    this.pollEventResponseSubject.next([
      {
        '@type': 'GlobalEvent',
        action: 'blockTab'
      }
    ]);
  }

  public wipeSessionData() {
    // unsubscribe from rfq observables
    clearInterval(this._currentTimestampInterval);
    this.latestGetEventsSubscription?.unsubscribe();
    this.latestGetEventsSubscription = null;
    this.initialEntityAccounts = [];
    this.providers = [];
  }

  public logout() {
    this.wipeSessionData();
    this.clearSelectedMedusaAccount();
    this.loginStateSubject.next(false);
    // block all tabs from rfqing
    this.blockAllTabs();
  }

  private get medusaUser(): MedusaUser {
    return this.adminService.medusaUser;
  }

  private get medusaURL(): string {
    const user = this.medusaUser;
    return user ? user.url : '';
  }

  /**
   * Some Medusa requests need to go to Hydra on a per-path basis.
   * Initially, this is only for CACIB.
   *
   * @returns the base URL to use for the given path, or null if no override
   */
  private medusaPathProxy(path: string): string {
    if (!this.medusaUser.url_by_path_parameter) {
      return undefined;
    }

    // need to strip a leading slash (but we search with and without, just in case)
    // and also any query string
    const path1 = path.replace(/[?].*/, '');
    const path2 = path1.replace(/^\//, '');

    let ret =
      this.medusaUser.url_by_path_parameter[path1] ||
      this.medusaUser.url_by_path_parameter[path2];

    return ret;
  }

  public isLegacyWhitelabel(): boolean {
    const user = this.medusaUser;
    return !!user?.useMedusalegacyWhitelabel;
  }

  protected get isMedusaProxy(): boolean {
    return this.medusaURL.indexOf('proxy') > -1;
  }

  protected medusaRequest(
    method: 'GET' | 'POST',
    urlPath: string,
    payload?: any,
    options?: MedusaRequestOptions
  ): Observable<any> {
    options = {
      ignoreError: false,
      logError: true,
      httpOptions: {},
      ...options
    };

    if (!this.medusaURL) {
      throw new Error('Medusa URL not found - is user logged in?');
    }
    const pathProxy = this.medusaPathProxy(urlPath);
    const medusaURL = pathProxy || this.medusaURL;
    const isSwitchUserLogin =
      urlPath.includes('/session/switchUser') && this.enableMedusaJWTLogin;

    return this.authService.currentSession$().pipe(
      switchMap(cognito => {
        let headers: HttpHeaders = new HttpHeaders(
          options.httpOptions.headers || {}
        );
        if (isSwitchUserLogin || this.isMedusaProxy || pathProxy) {
          headers = headers
            .append(
              `Authorization`,
              `Bearer ${cognito.getIdToken().getJwtToken()}`
            )
            .append('Content-Type', 'application/json');
        }

        const httpOptions = {
          withCredentials: true,
          ...options.httpOptions,
          headers,
          body: payload
        };

        let path =
          medusaURL.replace(/\/$/, '') + '/' + urlPath.replace(/^\//, '');

        const obs = this.http.request(method, path, httpOptions).pipe(
          catchError(err => {
            if (isSwitchUserLogin) {
              console.error('Medusa JWT login error', err);
              return throwError(() => {
                return { error: { exception: 'MedusaJWTLoginError' } };
              });
            }
            return throwError(() => err);
          })
        );

        return this.handleMedusaResponse(obs, urlPath, options);
      })
    );
  }

  protected handleMedusaResponse(
    obs: Observable<any>,
    urlPath: string,
    options: MedusaRequestOptions
  ): Observable<any> {
    return obs.pipe(
      handleLegacyMedusaResponse(urlPath),
      catchError(err => {
        // Will this work in websocket subclass?
        if (err.error?.exception?.type === 'NotLoggedIn') {
          setTimeout(() => {
            this.logout();
          });
        }

        if (options?.ignoreError) {
          return of({ error: err });
        }
        if (options?.logError && err.error) {
          this.cwLogSvc.logError(err.error);
        }

        return this.handleMedusaError(err, urlPath);
      })
    );
  }

  private getMedusaRequest(urlPath: string): Observable<any> {
    const logCorrelationId = shortUuid();
    this.phxService.logEvent({
      type: 'medusa',
      category: 'request',
      urlPath,
      logCorrelationId
    });
    return this.medusaRequest('GET', urlPath).pipe(
      tap(event =>
        this.phxService.logEvent({
          logCorrelationId,
          type: 'medusa',
          category: 'response',
          urlPath,
          ...event
        })
      ),
      catchError(err => {
        this.phxService.logEvent({
          logCorrelationId,
          type: 'medusa',
          category: 'error',
          urlPath,
          error: err
        });
        throw err;
      })
    );
  }

  private postMedusaRequest(
    urlPath: string,
    payload: any,
    options?: MedusaRequestOptions
  ): Observable<any> {
    const logPayload = { ...payload };

    if (logPayload.password) {
      logPayload.password = '********';
    }

    const logCorrelationId = shortUuid();

    this.phxService.logEvent({
      logCorrelationId,
      type: 'medusa',
      category: 'request',
      urlPath,
      payload: logPayload
    });
    return this.medusaRequest('POST', urlPath, payload, options).pipe(
      tap(event =>
        this.phxService.logEvent({
          logCorrelationId,
          type: 'medusa',
          category: 'response',
          urlPath,
          ...event
        })
      ),
      catchError(err => {
        this.phxService.logEvent({
          logCorrelationId,
          type: 'medusa',
          payload,
          category: 'error',
          urlPath,
          error: err
        });
        throw err;
      })
    );
  }

  public tradeService<T>(
    payload: KeyValue,
    suppressError = false
  ): Observable<any> {
    this.phxService.logEvent({
      type: 'hydra',
      category: 'request',
      urlPath: '/fx/tradeService',
      payload
    });
    return this.authService.currentSession$().pipe(
      switchMap(cognito => {
        return this.http
          .post<T>(
            apiURL(`/fx/tradeService`, { task: payload.task }),
            payload,
            getHeaders(cognito)
          )
          .pipe(
            tap(response => {
              this.phxService.logEvent({
                type: 'hydra',
                category: 'response',
                urlPath: '/fx/tradeService',
                response
              });
            }),
            tap((response: unknown) => {
              const r = response as Outcome;
              if (r.outcome?.message) {
                const level = r.outcome?.level || 'warn';
                this.alertService.addAlert({
                  message: r.outcome.message,
                  type: level
                });
              }
            }),
            catchError(err => {
              if (suppressError) {
                return of(err);
              }
              const id = this.logHydraError(err, `/fx/tradeService`, payload);
              raygunError(err, `/fx/tradeService`, [payload.task]);
              return this.handleError('tradeService error', id)(err, {
                urlPath: '/fx/tradeService',
                payload
              });
            })
          );
      })
    );
  }

  public logHydraError(
    err: any,
    urlPath: string,
    payload: any,
    errorId?: string
  ) {
    if (!errorId) {
      errorId = shortUuid();
    }
    this.phxService.logEvent({
      errorId,
      type: 'hydra',
      category: 'error',
      urlPath,
      payload,
      error: err
    });
    return errorId;
  }

  public validateRfq(payload: {
    products: GenericLeg[];
    providers: string[];
    hedgeType: DefaultHedges;
    hedges?: MultiHedge[];
  }): Observable<ValidateRfq | unknown> {
    return this.authService.currentSession$().pipe(
      switchMap(cognito => {
        return this.http.post<ValidateRfq>(
          apiURL('/fx/validate_rfq'),
          payload,
          getHeaders(cognito)
        );
      }),
      catchError(err => {
        raygunError(err, '/fx/validate_rfq', []);
        const id = shortUuid();
        this.logHydraError(err, `/fx/validate_rfq`, payload, id);
        return this.handleError('validate_rfq error', id)(err, {
          payload,
          urlPath: '/fx/validate_rfq'
        });
      })
    );
  }

  /**
   * Generic Hydra error handler.
   * @param operation The name of the request type
   * @param id Optional request UUID
   * @param errorMessagePaths Array of object paths to search first for the error message
   * @returns
   */
  private handleError(
    operation: string,
    id: string,
    errorMessagePaths?: string[]
  ) {
    return (
      error: HttpErrorResponse,
      meta?: ApiErrorMeta
    ): Observable<never> => {
      console.error(`handleError (${operation})`, error);
      const message = getErrorMessage(error, errorMessagePaths);
      // This also logs to Phx
      this.alertsService.addAlert({
        message,
        id,
        error,
        payload: meta?.payload,
        urlPath: meta?.urlPath
      });
      throw new Error(`${operation} failed: ${message}`);
    };
  }

  private handleMedusaError(
    interceptorData: any,
    urlPath: string
  ): Observable<never> {
    if (interceptorData.exception) {
      interceptorData = {
        response: {
          error: interceptorData.exception,
          url: urlPath
        }
      };
    }
    let exception = interceptorData.response?.error?.['exception'];

    let type: string = exception?.type;
    // Legacy LOWL API returns this error message in a different way
    let message: string = exception?.message;
    if (message?.endsWith('AccountLockedException')) {
      type = 'AccountLockedException';
    }

    if (type) {
      // handle exceptions if there
      switch (type) {
        case 'QuoteExpiredException':
          break;
        case 'AccountLockedException':
          this.logoutAndClearRfq();
          this.emitErrorMessage(
            'Account locked please contact DV.',
            interceptorData,
            { type }
          );
          break;
        case 'NotLoggedIn':
          this.logout();
          this.emitErrorMessage(
            'Account no longer logged in',
            interceptorData,
            { type }
          );
          break;
        default:
          this.handleGenericErrorMessages(interceptorData);
      }
    } else {
      this.handleGenericErrorMessages(interceptorData);
    }

    console.error(interceptorData);
    raygunError(interceptorData, urlPath, ['medusa']);

    return throwError(
      () => new Error(interceptorData.response ?? interceptorData)
    );
  }

  public getProviders(): Observable<{
    result: dvSchemas['ProviderSummaryDTO'][];
  }> {
    if (this.isLegacyWhitelabel()) {
      return of({ result: [] });
    } else {
      return this.getMedusaRequest('/provider/getProviders');
    }
  }

  public getClientAccounts(products?: GenericLeg[]): Observable<{
    result: dvSchemas['AccountSummaryDTO'][];
  }> {
    if (this.isLegacyWhitelabel()) {
      return of({ result: [] });
    } else {
      if (products) {
        if (!this.configService.userSettings().isWhiteLabelUser) {
          throw 'isWhiteLabelUser must be true to do a post request.';
        }
        return this.postMedusaRequest('/rfq/getClientAccounts', { products });
      } else {
        return this.getMedusaRequest('/rfq/getClientAccounts');
      }
    }
  }

  public get subscribeClientRFQs(): Observable<BooleanResponse> {
    return this.getMedusaRequest('/rfq/subscribeClientRFQs');
  }

  public get getEventsHttpRequest(): Observable<EventNotificationResult> {
    return this.postMedusaRequest(
      `/events/getEvents?sessionId=${window.sessionId}`,
      { since: this.lastEventTimestamp }
    ).pipe(
      tap((r: EventNotificationResult) => {
        this.lastEventTimestamp = r.result.timestamp;
      })
    );
  }

  public adjustQuoteForMargin(payload: {
    products: Record<string, any>[];
    clientQuoteEvent: ClientQuoteEventExtended;
    RFQDetailsDTO: RFQDetailsDTO;
  }): Observable<AdjustQuoteForMargin> {
    return this.authService.currentSession$().pipe(
      switchMap(cognito => {
        return this.http
          .post<{ response: AdjustQuoteForMargin }>(
            apiURL('/fx/adjustQuoteForMargin'),
            payload,
            getHeaders(cognito)
          )
          .pipe(map(resp => resp.response));
      }),
      catchError(err => {
        const id = shortUuid();
        this.logHydraError(err, '/fx/adjustQuoteForMargin', payload, id);
        raygunError(err, '/fx/adjustQuoteForMargin', []);
        return this.handleError('adjustQuoteForMargin error', id)(err, {
          payload,
          urlPath: '/fx/adjustQuoteForMargin'
        });
      })
    );
  }

  public listClientTradesByCriteria(
    payload: Required<dvSchemas['ListClientTradesByCriteria']>
  ): Observable<{
    result: dvSchemas['TradeDetailsDTO'][];
  }> {
    return of({ result: [] });
  }

  public async medusaLogout(): Promise<void> {
    if (this.medusaUser) {
      this.latestGetEventsSubscription?.unsubscribe();
      this.adminService.medusaAccount.sessionId = null;
      try {
        return firstValueFrom(this.getMedusaRequest('/session/logout'));
      } catch {
        return Promise.resolve();
      }
    }
  }

  private passwordExpired(): Observable<boolean> {
    return this.getMedusaRequest('/session/isPasswordExpired').pipe(
      map(r => r.result as boolean)
    );
  }

  private loginHttpRequest(
    payload: Required<dvSchemas['Login']>,
    additionalOptions: any = {}
  ): Observable<{ result: dvSchemas['LoginToken'] }> {
    additionalOptions = { ...additionalOptions, withCredentials: true };
    return this.postMedusaRequest('/session/login', payload, additionalOptions);
  }

  private switchUserHttpRequest(
    payload: Required<dvSchemas['SwitchUser']>,
    additionalOptions: any = {}
  ): Observable<{ result: dvSchemas['LoginToken'] }> {
    additionalOptions = { ...additionalOptions, withCredentials: true };
    return this.postMedusaRequest(
      '/session/switchUser',
      payload,
      additionalOptions
    );
  }

  public getDiscountFactor(payload: {
    product: KeyValue;
    task: 'getDefaultPricingCurrencySpotToForwardDiscountFactor';
  }): Observable<DiscountFactorResponse> {
    return this.authService.currentSession$().pipe(
      switchMap(amazonCognito => {
        return this.http.post<DiscountFactorResponse>(
          apiURL('/fx/marketDataService', { task: payload.task }),
          payload,
          getHeaders(amazonCognito)
        );
      })
    );
  }

  public getLiveSpot(payload: {
    cross: string;
    task: 'getLiveSpot';
  }): Observable<LiveSpotResponse> {
    return this.authService.currentSession$().pipe(
      switchMap(amazonCognito => {
        return this.http.post<LiveSpotResponse>(
          apiURL('/fx/marketDataService'),
          payload,
          getHeaders(amazonCognito)
        );
      })
    );
  }

  public getExecutionContext(
    payload: ExecutionContextPayload
  ): Observable<ExecutionContext> {
    payload.task = 'getExecutionContext';
    payload = this.entityMapperService.cleanConfidentialFields(
      clone(payload),
      'getExecutionContext'
    );
    return this.authService.currentSession$().pipe(
      switchMap(cognito => {
        return this.http
          .post<ExecutionContext>(
            getUserInfoURL(payload.task),
            payload,
            getHeaders(cognito)
          )
          .pipe(
            catchError(err => this.handleMedusaError(err, 'get-user-info'))
          );
      })
    );
  }

  public sendDealerIntervention(
    event: ClientQuoteEventExtended
  ): Observable<void> {
    return this.authService
      .fxPost<void>({ event }, 'handleDealerIntervention', '')
      .pipe(
        catchError(err =>
          this.handleMedusaError(err, 'handleDealerIntervention')
        )
      );
  }

  public getSubAccounts(account: string): Observable<SubAccount[]> {
    const payload = { task: 'getSubAccounts', account };
    return this.authService
      .fxPost<SubAccount[]>(payload, 'get-user-info', `task=${payload.task}`)
      .pipe(catchError(err => this.handleMedusaError(err, 'get-user-info')));
  }

  public getMedusaMarketCodeOrFixingSource(payload: {
    products: KeyValue[];
    task: 'getMedusaMarketCodeOrFixingSource';
  }): Observable<MedusaCodeAndType> {
    return this.authService.currentSession$().pipe(
      switchMap(cognito => {
        return this.http
          .post<MedusaCodeAndType>(
            'https://' +
              environment.api_domain +
              `.fxexotics.com/fx/tradeService?task=${payload.task}`,
            payload,
            getHeaders(cognito)
          )
          .pipe(catchError(err => this.handleMedusaError(err, 'tradeService')));
      })
    );
  }

  public submitDTORequest(
    url: string,
    payload: Required<SubmitRequest>
  ): Observable<{ result: dvSchemas['RFQDetailsDTO'] }> {
    payload = clone(payload);
    // account and primeBroker can have extra fields, which might be commercially sensitive
    payload.request.account = pick(payload.request.account, ['id', 'name']);
    payload.request.primeBroker = pick(payload.request.primeBroker, [
      'id',
      'name',
      'shortName'
    ]);
    this.phxService.logGetEvents(payload);
    return this.postMedusaRequest(`/rfq/${url}/submitRequest`, payload).pipe(
      tap(r => this.phxService.logGetEvents(r))
    );
  }

  public createDTO(
    url: string,
    payload: Partial<dvSchemas['Create']>
  ): Observable<{ result: dvSchemas['RFQDetailsDTO'] }> {
    return this.postMedusaRequest('/rfq/' + url + '/create', payload);
  }

  public acceptQuoteById(
    payload: Required<dvSchemas['AcceptQuoteById']>
  ): Observable<KeyValueResponse> {
    return this.postMedusaRequest('/rfq/acceptQuoteById', payload);
  }

  public getRfqTimeline(
    requestId: number
  ): Observable<{ events: RfqTimelineEvent[] }> {
    const payload = { task: 'getRfqTimeline', requestId };
    return this.authService
      .fxPost<{ events: RfqTimelineEvent[] }>(
        payload,
        'get-user-info',
        `task=${payload.task}`
      )
      .pipe(catchError(err => this.handleMedusaError(err, 'get-user-info')));
  }

  /**
   * This function has *excessive* amounts of logging to help track down a
   * bug where the picked up event is occasionally not recorded (to Hydra).
   * Unclear if f/e or b/e issue.
   *
   * Can pare down once the bug is found and move move to its natural home
   * in RFQTabService.handleClientQuoteEvent
   */
  private recordPickedUpEvent(event: ClientQuoteEventExtended): void {
    event = clone(event);
    delete event['processed'];
    const urlPath = '/fx/rfq/recordPickedUpEvent';
    const logCorrelationId = shortUuid();

    setTimeout(async () => {
      await this.phxService.sendToLobby('record_pickedup_event', {
        ...event,
        __state: 'request'
      });
    });

    this.phxService.logEvent({
      type: 'hydra',
      category: 'request',
      urlPath: '/fx/rfq/recordPickedUpEvent',
      event,
      logCorrelationId
    });

    this.authService
      .doPost<{}>({ event }, 'rfq/recordPickedUpEvent', '')
      .pipe(
        catchError(error => {
          const errorId = shortUuid();
          this.phxService.logEvent({
            type: 'hydra',
            category: 'error',
            urlPath,
            error: error.error || error,
            logCorrelationId,
            errorId
          });
          raygunError(error, urlPath, []);

          setTimeout(async () => {
            await this.phxService.sendToLobby('record_pickedup_event', {
              __state: 'error',
              __errorId: errorId,
              sourceEvent: event,
              error: error.error || error
            });
          });
          setTimeout(() => this.doSlackRecord(event, error));
          return this.handleError('recordPickedUpEvent error', errorId)(error, {
            payload: { event, urlPath: 'rfq/recordPickedUpEvent' }
          });
        })
      )
      .subscribe({
        next: () => {
          console.log('Picked Up Event Recorded');
          setTimeout(async () => {
            await this.phxService.sendToLobby('record_pickedup_event', {
              ...event,
              __state: 'success'
            });
          });
        }
      });
  }

  private doSlackRecord(event: any, err: any) {
    let message = ':boom: Failed to record PickedUp event';

    if (event) {
      message += '\n```\n' + JSON.stringify(event) + '\n```';
    }
    if (err.error) {
      if (err.error.message) {
        message += '\n\n```' + err.error.message + '\n```';
      } else {
        message += '\n\n```' + JSON.stringify(err.error) + '\n```';
      }
    }
    this.phxService.sendSlack(message);
  }

  public submitSetupEvent(requestId: number, setup: any): Observable<{}> {
    const payload = this.entityMapperService.cleanConfidentialFields({
      requestId,
      setup
    });
    return this.authService
      .fxPost<{}>(payload, 'rfq/submitSetupEvent', '')
      .pipe(
        catchError(err => {
          raygunError(err, '/fx/submitSetupEvent', []);
          return this.handleError('submitSetupEvent error', undefined, [
            'error.error.message'
          ])(err, { payload, urlPath: '/fx/submitSetupEvent' });
        })
      );
  }

  public submitPricingUpdateRequestEvent(payload: any): Observable<{}> {
    payload = this.entityMapperService.cleanConfidentialFields(payload);
    return this.authService
      .fxPost<{}>(payload, 'rfq/submitPricingUpdateRequestEvent', '')
      .pipe(
        catchError(err => {
          raygunError(err, '/fx/submitPricingUpdateRequestEvent', []);
          return this.handleError(
            'submitPricingUpdateRequestEvent error',
            undefined,
            ['error.error.message']
          )(err, { payload, urlPath: '/fx/submitPricingUpdateRequestEvent' });
        })
      );
  }

  public acceptQuote(
    payload: Required<dvSchemas['AcceptQuote']>
  ): Observable<KeyValueResponse> {
    return this.postMedusaRequest('/rfq/acceptQuote', payload);
  }

  public cancelRfq(
    payload: Required<dvSchemas['Cancel']>
  ): Observable<BooleanResponse> {
    return this.postMedusaRequest('/rfq/cancel', payload, {
      ignoreError: true
    });
  }

  public getAvailableParties(
    payload: Required<dvSchemas['GetAvailableParties']>
  ): Observable<{
    result: dvSchemas['AccountAvailableParties'];
  }> {
    if (this.isLegacyWhitelabel()) {
      return of({ result: { primeBrokerList: [] } });
    } else {
      return this.postMedusaRequest('/rfq/getAvailableParties', payload);
    }
  }

  public getEnabledAvailableProviders(
    payload: Required<dvSchemas['GetAvailableProviders']>
  ): Observable<{
    result: dvSchemas['AccountPrimeBrokerAvailableProviders'];
  }> {
    return this.postMedusaRequest(
      '/rfq/getEnabledAvailableProviders',
      payload
    ).pipe(tap(r => (this.availableProviders = r.result)));
  }

  public getAvailableSubstitutes(
    payload: Required<dvSchemas['GetAvailableSubstitues']>
  ): Observable<{
    result: dvSchemas['ProviderSummaryDTO'][];
  }> {
    return this.postMedusaRequest('/rfq/getAvailableSubstitues', payload);
  }

  public substituteProvider(
    payload: Required<dvSchemas['Substitute']>
  ): Observable<{
    result: dvSchemas['ProviderQuoteDTO'];
  }> {
    return this.postMedusaRequest('/rfq/substitute', payload);
  }

  public calculateSpotDate(
    payload: Required<dvSchemas['CalculateSpotDate']>
  ): Observable<{ result: number }> {
    if (this.isLegacyWhitelabel()) {
      return this.createDTO('call', {
        quotePair: payload.quotePair
        // tenorString: reformatDate(payload.currentDate, 'YYYY-MM-DD')
      }).pipe(map(r => ({ result: r.result.spotDate })));
    } else {
      return this.postMedusaRequest(
        '/dateCalculator/calculateSpotDate',
        payload
      );
    }
  }

  public subscribeProviderRFQs(): Observable<BooleanResponse> {
    return this.getMedusaRequest('/rfq/subscribeProviderRFQs');
  }

  public setOwner(payload: Required<dvSchemas['Setowner']>): Observable<{
    result: dvSchemas['ProviderQuoteDTO'];
  }> {
    return this.postMedusaRequest('/rfq/setowner', payload);
  }

  public pickup(payload: Required<dvSchemas['Pickup']>): Observable<{
    result: dvSchemas['ProviderQuoteDTO'];
  }> {
    return this.postMedusaRequest('/rfq/pickup', payload, {
      ignoreError: true,
      logError: false
    });
  }

  public openQuotes(): Observable<{
    result: dvSchemas['ProviderQuoteDTO'][];
  }> {
    return this.getMedusaRequest('/rfq/getOpenQuotes').pipe(
      map((response: { result: dvSchemas['ProviderQuoteDTO'][] }) => {
        return { result: response.result };
      })
    );
  }

  public withdraw(
    payload: Required<dvSchemas['Withdraw']>
  ): Observable<BooleanResponse> {
    return this.postMedusaRequest('/rfq/withdraw', payload);
  }

  public withdrawAll(): Observable<BooleanResponse> {
    return this.getMedusaRequest('/rfq/withdrawAll');
  }

  public decline(
    payload: Required<dvSchemas['Decline']>
  ): Observable<BooleanResponse> {
    return this.postMedusaRequest('/rfq/decline', payload);
  }

  public declineAll(): Observable<BooleanResponse> {
    return this.getMedusaRequest('/rfq/declineAll');
  }

  public submitProviderQuote(
    payload: Required<dvSchemas['SubmitProviderQuote']>
  ): Observable<BooleanResponse> {
    return this.postMedusaRequest('/rfq/submitProviderQuote', payload, {
      ignoreError: true
    });
  }

  public releaseQuote(
    payload: Required<dvSchemas['ReleaseQuote']>
  ): Observable<BooleanResponse> {
    return this.postMedusaRequest('/rfq/releaseQuote', payload);
  }

  public confirmAcceptance(
    payload: Required<dvSchemas['ConfirmAcceptance']>
  ): Observable<BooleanResponse> {
    return this.postMedusaRequest('/rfq/confirmAcceptance', payload, {
      ignoreError: true
    });
  }

  public declineRFE(
    payload: Required<dvSchemas['DeclineRFE']>
  ): Observable<BooleanResponse> {
    return this.postMedusaRequest('/rfq/declineRFE', payload);
  }

  public userRole(src: string): Observable<{ result: UserRole }> {
    return this.getMedusaRequest(`/session/userRole?src=${src}`);
  }

  public validateProducts(products: any[]): boolean {
    return products.every(product => {
      for (const field of [
        'forwardPointsBid',
        'forwardPointsAsk',
        'volBid',
        'volAsk'
      ]) {
        if (product[field] !== undefined && isNaN(parseFloat(product[field]))) {
          return false;
        }
      }
      return true;
    });
  }

  private handleGenericErrorMessages(requestResponse: RequestResponse) {
    // handles generic error message, because there are no valid exception that can be handled
    const response = requestResponse.response;
    let apiFunctionName: string;
    if (response?.url) {
      let url = response.url;
      const idx = url.indexOf('?'); // in case of query params
      if (idx >= 0) {
        url = url.substring(0, idx);
      }
      apiFunctionName = url.split('/').pop(); // get the function name of the api requested
    }
    // Hydra proxy/WL tries to send better error messages than Medusa
    let errorMessage: string;
    const error = response?.error;
    const type = error?.exception?.type;
    if (type && ERROR_TYPE_MAP.get(type)) {
      errorMessage = `Error submitting request - ${ERROR_TYPE_MAP.get(type)}`;
    } else if (error?.errorMessage) {
      errorMessage = error.errorMessage;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (error?.exception?.message) {
      errorMessage = error.exception.message;
    } else {
      errorMessage = generalErrorMessage(apiFunctionName);
    }

    const ctx = {
      source: 'handleGenericErrorMessages',
      api: apiFunctionName,
      requestResponse
    };

    switch (apiFunctionName) {
      case 'login':
        this.logout();
        this.emitErrorMessage(errorMessage, requestResponse, ctx);
        break;
      case 'getEvents':
        break;
      case 'listClientTradesByCriteria':
        this.emitErrorMessage(errorMessage, requestResponse, ctx);
        break;
      case 'userRole':
        // Don't show alert for userRole errors
        break;
      default:
        this.emitErrorMessage(errorMessage, requestResponse, ctx);
        break;
    }
  }

  public emitErrorMessage(
    errorMessage: any,
    requestResponse?: RequestResponse,
    ctx?: any
  ) {
    let urlPath: string = undefined;
    if (requestResponse?.response?.url) {
      urlPath = requestResponse.response.url;
      try {
        const url = new URL(urlPath);
        urlPath = url.pathname;
      } catch {}
    }
    if (typeof errorMessage !== 'string') {
      errorMessage = JSON.stringify(errorMessage);
    }
    if (!this.suppressErrorAlerts) {
      const id = shortUuid();
      const payload =
        requestResponse?.requestData?.['payload'] ||
        requestResponse?.requestData ||
        undefined;
      const error = requestResponse?.response?.error || undefined;
      this.phxBus.addEvent({
        category: 'error',
        type: 'medusa',
        payload,
        errorId: id,
        urlPath,
        error,
        message: errorMessage,
        context: ctx
      });
      this.alertsService.addAlert({
        message: errorMessage,
        urlPath,
        id,
        payload,
        error
      });
    }
  }

  public resetPassword(payload: {
    task: string;
    medusaUser: string;
    password: string;
  }): Promise<Observable<any>> {
    return this.authService.currentSession().then(cognito => {
      return this.http
        .post(getUserInfoURL(payload.task), payload, getHeaders(cognito))
        .pipe(catchError(err => this.handleMedusaError(err, 'get-user-info')));
    });
  }

  public recordSubmitRequest(payload: any): void {
    payload = this.entityMapperService.cleanConfidentialFields(
      clone(payload),
      '/fx/recordSubmitRequest'
    );
    this.authService
      .currentSession$()
      .pipe(
        switchMap(cognito => {
          return this.http
            .post<MedusaCodeAndType>(
              'https://' +
                environment.api_domain +
                `.fxexotics.com/fx/recordSubmitRequest`,
              payload,
              getHeaders(cognito)
            )
            .pipe(
              catchError(err =>
                this.handleMedusaError(err, 'recordSubmitRequest')
              )
            );
        })
      )
      .subscribe();
  }

  private makeRfqTab(
    legs: RfqBuilderLeg[],
    productLongNameToUse: string,
    presetSetupData: PresetSetupData
  ): RfqTab {
    const newRfqTab: RfqTab = {
      title: {
        status: {
          statusType: this.blockNewRfqRequests
            ? (RFQ_STATUS_BLOCKED as RfqStatus)
            : null,
          statusExtra: null
        }
      },
      positions: null,
      quoteOptions: {
        showPayRecieve: true,
        showQuotedPremiumSection: true
      },
      static: !!legs,
      maxDimensions: { height: null, width: null },
      productLongNameToUse,
      strategyBrokenDown: !!productLongNameToUse,
      presetSetupData
    };
    newRfqTab.dimensions = { height: 810, width: 1100 };
    newRfqTab.minDimensions = {
      height: null,
      width: null,
      heightWithTopOfBook: null
    };
    newRfqTab.topOfBook = false;
    newRfqTab.banksActive = 0;
    newRfqTab.legs = [];

    if (legs) {
      this.setRfqTabGenericInfo(newRfqTab, legs, true);
    } else {
      const shortName = newRfqTab.title.productName?.shortName;
      newRfqTab.oneWayView = setOneWayMode(
        shortName,
        legs,
        this.userStorageService.getItem<RfqSettingsState>(
          RfqSettingsStateKey
        ) || {},
        newRfqTab.static
      );
    }
    return newRfqTab;
  }

  public breakDownVanillaStrategy(
    products: Record<string, any>[]
  ): Observable<BreakDownVanillaStrategy> {
    return this.authService.currentSession$().pipe(
      switchMap(cognito => {
        return this.http.post<BreakDownVanillaStrategy>(
          apiURL('/fx/breakDownVanillaStrategy'),
          {
            products
          },
          getHeaders(cognito)
        );
      }),
      catchError(err => {
        const id = shortUuid();
        this.logHydraError(
          err,
          '/fx/breakDownVanillaStrategy',
          { products },
          id
        );
        raygunError(err, '/fx/breakDownVanillaStrategy', []);
        return this.handleError('breakDownVanillaStrategy error', id)(err, {
          payload: { products },
          urlPath: '/fx/breakDownVanillaStrategy'
        });
      })
    );
  }

  public get isRfqDisabled(): boolean {
    return (
      !this.adminService.isMedusaAccountLoggedIn ||
      !this.productsConfigService.rfqProductsGroupings ||
      (!this.configService.userSettings().isWhiteLabelUser &&
        !this.initialEntityAccounts?.length)
      // || !this.isLegacyWhitelabel() i don't think this ever got hit?
    );
  }

  public get showRfq(): boolean {
    return (
      !!this.adminService.medusaCredentials?.length &&
      this.currentUserRole() !== 'ProviderTrader'
    );
  }

  public getRfqDetails(requestId: number): Observable<RfqDetails> {
    const payload = { task: 'getRfqDetails', requestId };
    return this.authService
      .fxPost<RfqDetails>(payload, 'get-user-info', `task=${payload.task}`)
      .pipe(
        tap(response => {
          if (response?.setup) {
            this.displayedAccount.set(response.setup?.account);
          }
        }),
        catchError(err => this.handleMedusaError(err, 'get-user-info'))
      );
  }

  public getRfqTradeCaptureParams(): Observable<RfqTradeCaptureParams[]> {
    const payload = { task: 'getRfqTradeCaptureParams' };
    return this.authService
      .fxPost<{ rfqTradeCaptureParams: RfqTradeCaptureParams[] }>(
        payload,
        'get-user-info',
        `task=${payload.task}`
      )
      .pipe(
        map(r => r.rfqTradeCaptureParams),
        catchError(err => this.handleMedusaError(err, 'get-user-info'))
      );
  }

  public recordRfqOutcome(
    requestId: number,
    status: RfqOutcome
  ): Observable<{}> {
    const payload = { task: 'recordRfqOutcome', requestId, status };
    return this.authService
      .fxPost<{}>(payload, 'get-user-info', `task=${payload.task}`)
      .pipe(catchError(err => this.handleMedusaError(err, 'get-user-info')));
  }

  public getAdditionalAttributesApi(data: {
    lambda: string;
    payload: Record<string, any>;
  }): Observable<Record<string, any>> {
    const apiPath = `/fx/${data.lambda}` as FX_API_PATHS;
    return this.authService.currentSession$().pipe(
      switchMap(cognito => {
        return this.http
          .post<Record<string, any>>(
            apiURL(apiPath, { task: data.payload.task }),
            data.payload,
            getHeaders(cognito)
          )
          .pipe(
            catchError(err => {
              this.logHydraError(err, apiPath, data.payload, undefined);
              return this.handleMedusaError(err, data.lambda);
            })
          );
      })
    );
  }
}

let lastGetEventsErrorTime = 0;

function raygunError(error: any, urlPath: string, tags: string[]) {
  const path = urlPath.split('/').at(-1).split('?').at(0);
  if (path === 'getEvents') {
    // these can easily blow-up and exhaust our raygun limit so only log every 5 mins
    let delta = Date.now() - lastGetEventsErrorTime;
    if (delta > 1000 * 60 * 5) {
      raygunHttpError(error, [...tags, path]);
      lastGetEventsErrorTime = Date.now();
    }
    return;
  }
  raygunHttpError(error, [...tags, path]);
}

function setOneWayMode(
  shortName: string,
  legs: RfqBuilderLeg[],
  rfqSavedSettings: RfqSettingsState,
  isStatic = false
): OneWayView {
  const oneWayLoadedSetting =
    rfqSavedSettings?.['pricing-setting']?.['one-way'] || false;
  return {
    // if oneway is in settings load tab with this setting
    oneWay: false, // isStatic && legs.length === 1 ? false : oneWayLoadedSetting  // might be resurrected
    sideToShow: calcSideToShowOneWay(shortName, legs)
  };
}

function getHeaders(session: UserSession): { headers: HttpHeaders } {
  const token = session.getIdToken().getJwtToken();
  let headers = new HttpHeaders()
    .set('Content-Type', 'application/json')
    .set('Authorization', token);
  return { headers };
}

function generalErrorMessage(apiDirectory: string) {
  switch (apiDirectory) {
    case 'login':
      return 'Lost connection to server.';
    case 'create':
      return 'Unable to create RFQ.';
    case 'submitRequest':
      return 'Unable to submit request.';
    case 'cancel':
      return 'Unable to cancel request.';
    case 'listClientTradesByCriteria':
      return 'Blotter connection lost, please re-login the Trading Entity to continue viewing';
    default:
      if (apiDirectory) {
        return `An error occurred when calling ${apiDirectory}.`;
      } else {
        return `An unknown error occurred.`;
      }
  }
}

function handleLegacyMedusaResponse(urlPath: string) {
  return function (source: Observable<any>) {
    return source.pipe(
      map(response => {
        // Temporary check for any "old style" medusa responses with nested arrays. This
        // whole block can be removed once the old style responses are no longer used.
        // Probably very soon (Sept 2022).
        const r = response.result;
        // old api has items like this:
        // [['com.digitalvega.common.dto.AccountSummaryDTO', { name: 'ACC1', id: 1 }], ....]
        // We want to convert to [{ name: 'ACC1', id: 1 }, ...]
        if (Array.isArray(r) && r.length > 0 && Array.isArray(r[0])) {
          if (
            r.every(
              x =>
                x.length === 2 &&
                typeof x[0] === 'string' &&
                x[0].includes('vega')
            )
          ) {
            response.result = r.map(x => x[1]);
            console.warn(
              'Medusa nested array [CORRECTED]',
              urlPath,
              r,
              response.result
            );
          } else {
            console.error('Medusa nested array [UNCORRECTED]', urlPath, r);
          }
        }

        return response;
      })
    );
  };
}

function getErrorMessage(
  errorObj: HttpErrorResponse,
  errorMessagePaths?: string[]
): string {
  let m = null;
  if (errorMessagePaths) {
    for (let i = 0; i < errorMessagePaths.length; i++) {
      m = get(errorObj, errorMessagePaths[i]);
      if (typeof m === 'string') {
        return m;
      }
    }
  }
  if (typeof errorObj.error === 'string') {
    return errorObj.error;
  }
  if (typeof errorObj.error?.errorMessage === 'string') {
    return errorObj.error.errorMessage;
  }
  if (typeof errorObj.error?.message === 'string') {
    return errorObj.error.message;
  }
  if (typeof errorObj.error?.error?.message === 'string') {
    return errorObj.error.error.message;
  }
  if (typeof errorObj.message === 'string') {
    return errorObj.message;
  }
  if (typeof errorObj.error?.exception === 'string') {
    return errorObj.error?.exception;
  }
  return `server returned code ${errorObj.status} with body "${JSON.stringify(
    errorObj.error
  )}"`;
}
